// Choices stay on this page until the main product form is submitted.
class MerseyProductCapSelector extends HTMLElement {
  selected = new Map();
  busy = false;

  connectedCallback() {
    this.productForm = this.closest('product-form-component');
    this.rail = this.querySelector('.ma-cap-set__rail');
    this.addEventListener('click', this.onClick);
    this.rail?.addEventListener('scroll', this.updateArrows, { passive: true });
    this.productForm?.addEventListener('change', this.update);
    this.productForm?.addEventListener('quantity-selector:update', this.update);
    this.resizeObserver = new ResizeObserver(this.updateArrows);
    if (this.rail) this.resizeObserver.observe(this.rail);
    this.hidden = false;
    this.update();
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.rail?.removeEventListener('scroll', this.updateArrows);
    this.productForm?.removeEventListener('change', this.update);
    this.productForm?.removeEventListener('quantity-selector:update', this.update);
    this.resizeObserver?.disconnect();
  }
  getSelectedItems() {
    return Array.from(this.selected.keys(), variantId => ({ variantId, quantity: 1 }));
  }
  getSelectionSummary() {
    const quantity = Number(this.productForm?.querySelector('input[name="quantity"]')?.value) || 1;
    const count = quantity + this.selected.size;
    return {
      active: this.selected.size > 0,
      button: this.dataset.button.replace('[count]', String(count)),
      summary: this.dataset.stickySummary.replace('[count]', String(count))
    };
  }
  setBusy(value) {
    this.busy = value;
    this.setAttribute('aria-busy', String(value));
    this.querySelectorAll('button[data-cap-variant], button[data-remove-cap]').forEach(button => { button.disabled = value; });
  }
  reset() {
    this.selected.clear();
    this.update();
  }
  updateArrows = () => {
    if (!this.rail) return;
    this.querySelector('[data-cap-direction="-1"]').disabled = this.rail.scrollLeft <= 2;
    this.querySelector('[data-cap-direction="1"]').disabled = this.rail.scrollLeft + this.rail.clientWidth >= this.rail.scrollWidth - 2;
  };
  onClick = event => {
    if (!(event.target instanceof Element)) return;
    const arrow = event.target.closest('[data-cap-direction]');
    if (arrow) {
      this.rail.scrollBy({ left: Number(arrow.dataset.capDirection) * (this.rail.clientWidth - 4), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      return;
    }
    const button = event.target.closest('button[data-cap-variant],button[data-remove-cap]');
    if (!button || this.busy) return;
    const id = button.dataset.capVariant || button.dataset.removeCap;
    if (this.selected.has(id)) this.selected.delete(id);
    else if (this.selected.size < 2) this.selected.set(id, button.dataset.title);
    else {
      this.querySelector('.ma-cap-set__status').textContent = this.dataset.limit;
      return;
    }
    const removedChip = button.hasAttribute('data-remove-cap');
    this.update();
    if (removedChip) this.querySelector(`button[data-cap-variant="${id}"]`)?.focus({ preventScroll: true });
  };
  update = () => {
    const quantity = Number(this.productForm?.querySelector('input[name="quantity"]')?.value) || 1;
    const count = quantity + this.selected.size;
    const progress = this.querySelector('.ma-cap-progress');
    if (progress) {
      const step = Math.min(3, count);
      progress.setAttribute('aria-valuenow', String(step));
      progress.setAttribute('aria-valuetext', progress.dataset.progressText.replace('[count]', String(step)));
      progress.dataset.full = String(step === 3);
      progress.style.setProperty('--ma-cap-ratio', String(Math.max(0, (step - 1) / 2)));
      progress.querySelector('.ma-cap-progress__count').textContent = `${step}/3`;
      progress.querySelectorAll('[data-cap-step]').forEach(icon => { icon.dataset.reached = String(Number(icon.dataset.capStep) <= step); });
    }
    this.querySelectorAll('button[data-cap-variant]').forEach(button => {
      const chosen = this.selected.has(button.dataset.capVariant);
      button.setAttribute('aria-pressed', String(chosen));
      button.textContent = chosen ? this.dataset.selected : this.dataset.select;
      button.setAttribute('aria-label', (chosen ? this.dataset.removeLabel : this.dataset.selectLabel).replace('[title]', button.dataset.title));
    });
    this.querySelector('.ma-cap-set__status').textContent = this.selected.size ? this.dataset.summary.replace('[count]', String(count)) : this.dataset.instruction;
    const selection = this.querySelector('.ma-cap-set__selection');
    selection.replaceChildren();
    selection.hidden = this.selected.size === 0;
    for (const [id, title] of this.selected) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.dataset.removeCap = id;
      const name = document.createElement('span');
      name.className = 'ma-cap-set__selection-name';
      name.textContent = title;
      const remove = document.createElement('span');
      remove.className = 'ma-cap-set__selection-remove';
      remove.textContent = '×';
      remove.setAttribute('aria-hidden', 'true');
      chip.append(name, remove);
      chip.title = title;
      chip.setAttribute('aria-label', this.dataset.removeLabel.replace('[title]', title));
      chip.disabled = this.busy;
      selection.append(chip);
    }
    const addButton = this.productForm?.querySelector('button[name="add"]');
    if (addButton && !addButton.disabled) {
      // Preserve the native icon and confirmation animation while changing only its text node.
      const label = Array.from(addButton.childNodes).find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (label) {
        if (!this.defaultLabel) this.defaultLabel = label.textContent;
        const text = this.selected.size ? this.dataset.button.replace('[count]', String(count)) : this.defaultLabel;
        if (label.textContent !== text) label.textContent = text;
      }
    }
    this.dispatchEvent(new CustomEvent('ma:cap-selection-change', { bubbles: true }));
    this.updateArrows();
  };
}
if (!customElements.get('ma-product-cap-selector')) {
  customElements.define('ma-product-cap-selector', MerseyProductCapSelector);
}

